import React, { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useStoreon } from 'storeon/react';
import PropTypes from 'prop-types';

import Input from '../Input';
import Select from '../Select';
import Radios from '../Radios';
import Checkbox from '../Checkbox/Checkbox';
import CanadaStates from '../../constants/CanadaStates';
import USAStates from '../../constants/USAStates';
import emailValidationRules from '../../constants/validateEmail';

const BlockCustomerInfo = ( props ) => {
    const { index, number = true, showCheckbox = false, optionText = false, brand = 'MILGARD' } = props;
    const { watch, control, setValue, trigger, register, setError, clearErrors } = useFormContext();
    const { groups } = useStoreon( 'groups' );
    const { country, contact_method: selectedContact } = watch();
    const smsOptIn = watch('sms_opt_in', false);
    const mobilePhone = watch('mobile_phone');
    const refSelect = useRef();

    const isMobilePhoneRequired = selectedContact === 'Text';

    useEffect(() => {
        register({ name: 'sms_opt_in' });
    }, [register]);

    useEffect( () => {
        setValue('postal_code', '');
        refSelect.current.select.onChange('');
        setValue('administrative_area', '');
        setValue('locality', '');
        trigger();
    }, [country, trigger, setValue] );

    useEffect(() => {
        if (selectedContact === 'Text') {
            setValue('sms_opt_in', true);
            if (!mobilePhone || !/^[0-9]{10}$/.test(mobilePhone)) {
                setError('mobile_phone', {
                    type: 'manual',
                    message: 'Mobile phone is required when Text is selected.',
                });
            } else {
                clearErrors('mobile_phone');
            }
        } else {
            clearErrors('mobile_phone');
            setValue('sms_opt_in', false);
            trigger();
        }
    }, [selectedContact, mobilePhone, setValue, setError, clearErrors, trigger]);

    return (
        <fieldset className="form-block" disabled={groups[index].disabled}>
            <legend className="form-block__title">{number ? index + 1 + '. ' : ''} Product Owner Information</legend>

            <div className="row">
                <div className="col col-12 col-sm-6">
                    <Input
                        label="First name"
                        name="first_name"
                        rules={{
                            required: 'This field is required.',
                            pattern: {
                                value: /^.{1,20}$/,
                                message:
                                    'Please modify the first name to be 20 characters or less.',
                            },
                        }}
                    />
                </div>
                <div className="col col-12 col-sm-6">
                    <Input
                        label="Last name"
                        name="last_name"
                        rules={{
                            required: 'This field is required.',
                        }}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col col-12 col-sm-6 col-md-3">
                    <Select
                        label="Country"
                        name="country"
                        options={[
                            {
                                value: 'US',
                                label: 'United States',
                            },
                            {
                                value: 'CA',
                                label: 'Canada',
                            },
                        ]}
                        defaultValue='US'
                        rules={{
                            required: 'This field is required.',
                        }}
                    />
                </div>

                <div className="col col-12 col-sm-6 col-md-3">
                    <Input
                        label="City"
                        name="locality"
                        rules={{
                            required: 'This field is required.',
                        }}
                    />
                </div>

                <div className="col col-12 col-sm-6 col-md-3">
                    <Select
                        label={country === 'US' ? 'State' : 'Province'}
                        name="administrative_area"
                        options={country === 'US' ? USAStates : CanadaStates}
                        isSearchable
                        control={control}
                        rules={{
                            required: 'This field is required.',
                        }}
                        reset={refSelect}
                    />
                </div>

                <div className="col col-12 col-sm-6 col-md-3">
                    <Input
                        hint={{
                            info: 'The Postal Code should contain only letters and numbers. ex. A1A 1A1',
                        }}
                        label={country === 'US' ? 'Zip Code' : 'Postal Code'}
                        name="postal_code"
                        rules={{
                            required: 'This field is required.',
                            pattern: {
                                value:
                                    country === 'US'
                                        ? /(^\d{5}$)|(^\d{5}-\d{4}$)/
                                        : /^[A-Z][0-9][A-Z] [0-9][A-Z][0-9]+$/,
                                message:
                                    country === 'US'
                                        ? 'Please enter a valid zip code'
                                        : 'Please enter a valid postal code',
                            },
                        }}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col col-12 col-sm-6">
                    <Input
                        label="Street Address (NOT PO Box)"
                        name="thoroughfare"
                        rules={{
                            required: 'This field is required.',
                        }}
                    />
                </div>

                <div className="col col-12 col-sm-6">
                    <Input label="Street Address - Line 2 (Optional)" name="premise" isRequired={false}/>
                </div>
            </div>
            <div className="row">
                <div className="col col-12 col-sm-6">
                    <Input
                        label="Home Phone"
                        name="primary_phone"
                        rules={{
                            required: 'This field is required.',
                            pattern: {
                                value: /^[0-9]{10}$/,
                                message:
                                    'The home phone number should contain only 10 digits (ex. 5555555555) with no spaces.',
                            },
                        }}
                    />
                </div>
                <div className="col col-12 col-sm-6">
                    <Input
                        label="Mobile Phone"
                        name="mobile_phone"
                        isRequired={isMobilePhoneRequired}
                        rules={{
                            required: isMobilePhoneRequired ? 'Mobile phone is required when Text is selected.' : false,
                            pattern: {
                                value: /^[0-9]{10}$/,
                                message:
                                    'The mobile phone number should contain only 10 digits (ex. 5555555555) with no spaces.',
                            },
                        }}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col col-12 col-sm-6">
                    <Input
                        label="Email Address"
                        name="email_address"
                        type="email"
                        rules={emailValidationRules()}
                    />
                </div>
                <div className="col col-12 col-sm-6">
                    <Select
                        label="Customer Type"
                        name="customer_type"
                        options={[
                            {
                                value: 'Homeowner',
                                label: 'Homeowner',
                            },
                            {
                                value: 'Contractor/Builder',
                                label: 'Contractor/Builder',
                            },
                            {
                                value: 'Other',
                                label: 'Other',
                            },
                        ]}
                        isRequired={false}
                    />
                </div>
            </div>
            <Radios
                label="How Should We Contact You?"
                name="contact_method"
                options={[
                    {
                        value: 'Email',
                        label: 'Email',
                    },
                    {
                        value: 'Phone',
                        label: 'Phone',
                    },
                    ...(optionText
                        ? [
                            {
                                value: 'Text',
                                label: 'Text',
                            },
                        ]
                        : []),
                ]}
                rules={{
                    required: 'This field is required.',
                }}
            />
            {showCheckbox && (
                <div className="row">
                    <div className="col col-12 col-md-12 col-lg-12">
                        <div className="form-item form-item--checkbox">
                            <Checkbox
                                label={`I agree to receive text messages from ${brand} regarding my service request and related information. Message and data rates may apply.`}
                                name='sms_opt_in'
                                {...register('sms_opt_in')}
                                checked={smsOptIn}
                                onChange={(checked) => setValue('sms_opt_in', checked)}
                            />
                        </div>
                    </div>
                </div>
            )}

            {!groups[index].disabled && groups[index + 1] && groups[index + 1].disabled && (
                <div className={'form-block__error'}>
                    You must fill in all required fields prior to moving to the next section.
                </div>
            )}
        </fieldset>
    );
};

BlockCustomerInfo.propTypes = {
    index: PropTypes.number.isRequired,
    showCheckbox: PropTypes.bool,
    optionText: PropTypes.bool,
    brand: PropTypes.string,
};

BlockCustomerInfo.defaultProps = {
    showCheckbox: false,
    optionText: false,
};

export default BlockCustomerInfo;
